import { useState } from "react";

import { Button } from "common/components/ui/Button";
import { DialogBody, DialogFooter } from "common/components/ui/Dialog";
import { DrawerBody, DrawerFooter } from "common/components/ui/Drawer";
import useDebounce from "common/hooks/useDebounce";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useBlacklist from "../../../datahooks/useBlacklist";
import useBlacklistMutations from "../../../datahooks/useBlacklistMutations";
import useCompaniesSearch from "../../../datahooks/useCompaniesSearch";
import BlacklistItem from "../BlacklistItem";
import AddToBlacklistSearch from "./AddToBlacklistSearch";

import type { Company } from "../../../types";

import EmptyStateImage from "assets/images/empty-placeholders/blacklist.png";

export default function BlacklistCompanies({
  onClose,
}: {
  onClose: () => void;
}) {
  const isDesktop = useTwBreakpoint("lg");
  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const Body = isDesktop ? DialogBody : DrawerBody;
  const { addToBlacklist, isAddingToBlacklist } = useBlacklistMutations();
  const [keywords, setKeywords] = useState("");
  const [debouncedKeywords] = useDebounce(keywords, 600);
  const [selectedCompanies, setSelectedCompanies] = useState<Company[]>([]);
  const { companies, isLoadingCompanies } =
    useCompaniesSearch(debouncedKeywords);
  const { blacklist } = useBlacklist("companies");

  function addCompanies() {
    addToBlacklist({ companies: selectedCompanies }).then(() => {
      setSelectedCompanies([]);
      onClose();
    });
  }
  function removeCompany(companyUrn: string) {
    setSelectedCompanies(
      selectedCompanies.filter(({ urn }) => urn !== companyUrn),
    );
  }

  const searchResults = companies
    ?.filter(
      (company) =>
        !blacklist.find(({ urn }) => company.urn === urn) &&
        !selectedCompanies.find(({ urn }) => company.urn === urn),
    )
    .map((company) => {
      const { urn, logo_url, name, description } = company;
      return {
        id: urn,
        pictureUrl: logo_url,
        title: name,
        subtitle: description,
        originalObject: company,
      };
    });

  return (
    <>
      <Body className="flex-1 overflow-hidden">
        <AddToBlacklistSearch
          isLoading={isLoadingCompanies}
          searchText={keywords}
          setSearchText={setKeywords}
          searchResults={searchResults}
          onSelectResult={(company) => {
            setSelectedCompanies([...selectedCompanies, company]);
          }}
          type="companies"
        />
        {selectedCompanies.length ? (
          <div className="grid grid-cols-1 gap-4 overflow-y-auto max-lg:mb-4 lg:grid-cols-2 lg:px-6">
            {selectedCompanies.map(({ urn, logo_url, name, description }) => (
              <BlacklistItem
                key={urn}
                subtitle={description}
                title={name}
                pictureUrl={logo_url}
                onRemove={() => removeCompany(urn)}
              />
            ))}
          </div>
        ) : (
          <div className="mx-auto flex max-w-72 grow flex-col items-center justify-center gap-4 max-lg:my-4 lg:max-w-96">
            <img alt="" src={EmptyStateImage} width={200} />
            <h5 className="text-center text-2xl-bold">
              No companies to blacklist
            </h5>
            <p className="-mt-2 text-center text-black-500">
              Search companies you want to add to your blacklist. Once you are
              satisfied with your selection, click &quot;Add to blacklist&quot;
              to blacklist them
            </p>
          </div>
        )}
      </Body>
      <Footer className="justify-end">
        <Button
          disabled={!selectedCompanies.length}
          onClick={addCompanies}
          isLoading={isAddingToBlacklist}
          data-cy="add-to-blacklist-button"
        >
          Add to blacklist
        </Button>
      </Footer>
    </>
  );
}
